import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {Formik} from 'formik';
import moment from 'moment';
import {ConnectedFocusError} from 'focus-formik-error';
import DiscountV2Schema from 'helpers/services/formValidationSchemas/DiscountV2Schema';
import {DaysPickerContextProvider} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/context/DaysPickerContext';
import SettingsModal from 'routes/App/discountsV2/DiscountWizard/SettingsModal';
import {createBaseDiscount} from 'helpers/functionUtils/discountsV2';
import {DISCOUNT_TYPE} from 'helpers/constants/discountsV2';
import {
  CREATE_FEEDBACK_DISCOUNT,
  UPDATE_FEEDBACK_DISCOUNT
} from 'store/feedback/action';
import {useFeedbackDiscountSaving} from 'store/feedback/selectors';

const SINGLE_USE_FEATURE = 'one_time_coupon';

const DEFAULTS = {
  name: 'Feedback Reply Discount',
  value: 5,
  min: 25,
  code: '5NEXT4U'
};

const singleUsePerCustomer = (timezone) => ({
  type: 'PerCustomer',
  maxUsages: 1,
  activatesAtLocal: moment()
    .utcOffset(Number(timezone) || 0, false)
    .utc(true)
    .format()
});

const buildInitialValues = (discount, {allowsSingleUse, timezone}) => {
  if (discount) return {...discount};

  const base = createBaseDiscount();
  return {
    ...base,
    name: DEFAULTS.name,
    type: DISCOUNT_TYPE.WHOLE_CART,
    rate: {type: DISCOUNT_TYPE.FIXED, value: DEFAULTS.value},
    subtotal: {...base.subtotal, min: DEFAULTS.min},
    isPromotional: false,
    promoCode: {
      code: DEFAULTS.code,
      usageLimit: allowsSingleUse ? singleUsePerCustomer(timezone) : null
    },
    marketing: {
      ...base.marketing,
      feedbackReply: {isEnabled: true}
    },
    creationReason: 'IncreaseOrderVolume'
  };
};

const FeedbackDiscountFormModal = ({open, discount, onClose}) => {
  const dispatch = useDispatch();
  const saving = useFeedbackDiscountSaving();
  const {AllowedServiceFeatures, Timezone} = useSelector(
    (state) => state.user.activeLocation
  );

  // The promo code is what gets injected into the reply, so this discount has
  // no reason to exist without one.
  const [enabledDiscount, setEnabledDiscount] = useState(true);
  const [enabledDate, setEnabledDate] = useState(false);
  const [enabledCustomAvailability, setEnabledCustomAvailability] = useState(
    false
  );
  const [enabledAdvancedSettings, setEnabledAdvancedSettings] = useState(false);

  // The toggles live outside formik, so reopening the modal has to derive them
  // again, including back to off when creating after editing.
  useEffect(() => {
    const availability = discount?.availability;
    const hasDates = Boolean(
      availability?.activatesAtLocal || availability?.deactivatesAtLocal
    );
    const hasWeekly = Boolean(availability?.weekly);
    setEnabledDiscount(discount ? Boolean(discount.promoCode?.code) : true);
    setEnabledDate(hasDates);
    setEnabledAdvancedSettings(hasWeekly);
    setEnabledCustomAvailability(hasDates || hasWeekly);
  }, [discount, open]);

  const initialValues = useMemo(
    () =>
      buildInitialValues(discount, {
        allowsSingleUse: AllowedServiceFeatures.includes(SINGLE_USE_FEATURE),
        timezone: Timezone
      }),
    [discount, AllowedServiceFeatures, Timezone]
  );

  if (!open) return null;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={DiscountV2Schema}
      enableReinitialize
      onSubmit={(values, {setFieldError, setSubmitting}) => {
        const payload = {...values};

        if (!payload.promoCode?.code) {
          setFieldError('promoCode.code', 'Promo Code field cannot be empty!');
          setSubmitting(false);
          return;
        }

        if (
          enabledDate &&
          (!payload.availability?.activatesAtLocal ||
            payload.availability?.activatesAtLocal === 'Invalid date' ||
            !payload.availability?.deactivatesAtLocal ||
            payload.availability?.deactivatesAtLocal === 'Invalid date')
        ) {
          setFieldError(
            'availability.activatesAtLocal',
            'Please select start and end date!'
          );
          setSubmitting(false);
          return;
        }

        payload.promoCode = {
          ...payload.promoCode,
          code: payload.promoCode.code.toUpperCase()
        };
        payload.marketing = {
          ...payload.marketing,
          feedbackReply: {isEnabled: true}
        };

        if (payload.rate?.type === DISCOUNT_TYPE.PERCENTAGE) {
          payload.subtotal = {
            ...payload.subtotal,
            min: payload.subtotal?.min || 0
          };
        }

        const callbacks = {
          postCallback: () => {
            setSubmitting(false);
            onClose();
          },
          errorCallback: () => setSubmitting(false)
        };

        dispatch(
          discount?.id
            ? UPDATE_FEEDBACK_DISCOUNT({
                id: discount.id,
                discount: payload,
                ...callbacks
              })
            : CREATE_FEEDBACK_DISCOUNT({discount: payload, ...callbacks})
        );
      }}
    >
      {(formik) => (
        <>
          <ConnectedFocusError />
          <DaysPickerContextProvider>
            <SettingsModal
              discount={discount || initialValues}
              formik={formik}
              enabledDiscount={enabledDiscount}
              setEnabledDiscount={setEnabledDiscount}
              enabledDate={enabledDate}
              setEnabledDate={setEnabledDate}
              buttonStatus={saving}
              allowedServiceFeatures={AllowedServiceFeatures}
              enabledCustomAvailability={enabledCustomAvailability}
              setEnabledCustomAvailability={setEnabledCustomAvailability}
              enabledAdvancedSettings={enabledAdvancedSettings}
              setEnabledAdvancedSettings={setEnabledAdvancedSettings}
              isOpen={open}
              onCloseAction={onClose}
            />
          </DaysPickerContextProvider>
        </>
      )}
    </Formik>
  );
};

FeedbackDiscountFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  discount: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

FeedbackDiscountFormModal.defaultProps = {
  discount: null
};

export default FeedbackDiscountFormModal;
